import createFetchClient, { type Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { useMemo } from "react";

import type { paths } from "@/src/api/api"; // generated by openapi-typescript
import { useSession } from "@/src/contexts/AuthContext";
import { useLocale } from "@/src/i18n/i18n";
import { ApiError } from "@/src/libs/ApiError";
import { fetchWithTimeout } from "@/src/libs/fetchWithTimeout";
import { isTokenRejection } from "@/src/libs/isTokenRejection";

/**
 * Client d'API typé, branché sur React Query — portage du hook de wargame-app.
 *
 * `$api.useQuery` / `$api.useMutation` sont typés depuis `api/api.ts`, lui-même
 * généré depuis le `swagger.yml` du serveur (`bun run api:generate`).
 */
export const useApiClient = () => {
  const { session, signOut } = useSession();
  const { locale } = useLocale();
  const token = session?.token;

  // Mémoïsé sur le token **et la langue** : sans ça, un nouveau client est
  // recréé à chaque render, ce qui déstabilise toutes les callbacks qui en
  // dépendent et provoque des refetch en boucle. Changer de langue, en
  // revanche, doit bien reconstruire le client — c'est ce qui fait suivre
  // `Accept-Language` aux messages d'erreur venus du serveur.
  return useMemo(() => {
    const fetchClient = createFetchClient<paths>({
      baseUrl: import.meta.env.VITE_API_URL,
      // Borne toute requête à 30 s, sauf les uploads. Cf. `fetchWithTimeout`.
      fetch: fetchWithTimeout,
      headers: {
        // Sans jeton, **pas d'en-tête du tout** plutôt qu'un `Authorization`
        // vide : c'est ce qui permet au middleware ci-dessous de distinguer
        // « jeton refusé » de « requête partie sans jeton ».
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Accept-Language": locale,
      },
    });

    const throwOnError: Middleware = {
      async onResponse({ response, request }) {
        // ⚠️ **Un 401 ne vaut déconnexion que si la requête portait un jeton** —
        // cf. `isTokenRejection`.
        if (
          isTokenRejection(
            response.status,
            request.headers.get("Authorization"),
          )
        ) {
          signOut();
        }

        if (!response.ok) {
          let body: unknown = undefined;
          try {
            body = await response.clone().json();
          } catch {
            // Corps illisible (HTML d'un reverse proxy, réponse vide) :
            // l'`ApiError` se contentera du statut.
          }

          throw new ApiError(response.status, response.statusText, body);
        }
      },
    };

    fetchClient.use(throwOnError);

    return createClient(fetchClient);
  }, [token, locale, signOut]);
};

/**
 * Client `openapi-fetch` brut, pour les appels impératifs hors React Query.
 *
 * ⚠️ Pas de middleware ici : il ne lève pas d'`ApiError` et ne déconnecte pas
 * sur 401. Normalise ce qu'il renvoie avec `ApiError.from(...)`.
 */
export const useLazyApiClient = () => {
  const { session } = useSession();
  const { locale } = useLocale();
  const token = session?.token;

  return useMemo(
    () =>
      createFetchClient<paths>({
        baseUrl: import.meta.env.VITE_API_URL,
        fetch: fetchWithTimeout,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Accept-Language": locale,
        },
      }),
    [token, locale],
  );
};
